import { Injectable } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { isArray } from 'rxjs/internal-compatibility';
import { AddinfoGroup, Res } from '../models';
import { ApiService } from './api.service';
import { DatetimeService } from './datetime.service';

@Injectable({
  providedIn: 'root'
})
export class AddinfoService {

  selectorsSubject = new Subject<any>();
  focusMissingFieldSubject = new Subject<any>();
  selectors: any = {};
  currentValue: any[] = [];
  addinfoFormValue: any[];
  // спецификация атрибутов (имена, is_mandatory и т.п.) — нужна для валидации перед сохранением
  addinfoGroupsSpec: any[] = [];

  // используемые типы полей формы
  attrType = {
    input:                    'INPUT',
    input_multiple:           'INPUT_MULTIPLE',
    combobox:                 'COMBOBOX',
    combobox_multiple:        'COMBOBOX_MULTIPLE',
    combobox_filter:          'COMBOBOX_FILTER',
    textarea:                 'TEXTAREA',
    date:                     'DATE',
    datetime:                 'DATETIME',
    file:                     'FILE',
    file_multiple:            'FILE_MULTIPLE',
    checkbox:                 'CHECKBOX',
    radio_button:             'RADIO_BUTTON',
    certificate:              'CERTIFICATE',
    tags:                     'TAGS'
  }

  // используемые типы данных
  attrValueType = {
    string:    'STRING',
    boolean:   'BOOLEAN',
    integer:   'INTEGER',
    numeric:   'NUMERIC',
    date:      'DATE',
    datetime:  'DATETIME',
    timestamp: 'TIMESTAMP',
  }

  constructor(
    private api: ApiService,
    private datetimeService: DatetimeService
  ) { }


  /**
   * 
   * @param params any
   * @returns Observable<Res>
   */
  getAddinfoSpecifications(params: any): Observable<Res> {

    return this.api.post('addinfoList', params, 'private');
  }


  /**
   * 
   * @param params any
   * @returns Observable<Res>
   */
  getAddinfoData(methodName: string, params: any): Observable<Res> {

    return this.api.post(methodName, params, 'private');
  }


  /**
   * 
   * @param data any
   * @returns Observable<Res>
   */
  updateAddinfoData(data: any): Observable<Res> {

    return this.api.post('addinfoUpdate', data, 'private');
  }


  /**
   * 
   * @returns Observable<Res>
   */
  getSelectors(): Observable<Res> {

    return this.api.get('getPresetSelectors', '', 'private');
  }

  
  /**
   * Преобразование типа в String значения из поля attribute_value
   * @param groups any[]
   * @returns any[]
   */
  typeConversionToString(groups: any[]): any[] {

    for(let group of groups){

      for(let attr of group.attributes){
      
        delete attr.images;      // далее поле не требуется
        delete attr.files;       // далее поле не требуется
        delete attr.tags;        // далее поле не требуется
        delete attr.certificate; // далее поле не требуется

        if(attr.attribute_value != null && attr.attribute_value.day != null){ 

          attr.attribute_value = this.datetimeService.convertDateForDB(attr.attribute_value);
        }

        // null/undefined приводим к пустой строке — иначе бэкенд падает с DataIntegrityViolationException
        // на вставке (NOT NULL колонки в attribute_value).
        if (attr.attribute_value == null) {

          attr.attribute_value = '';
        }
        else if(typeof (attr.attribute_value) != 'object' || isArray(attr.attribute_value)){

          attr.attribute_value = String(attr.attribute_value); // переводим в string
        }

        if(attr.attribute_value === 'true' || attr.attribute_value === 'false'){

          attr.attribute_value = attr.attribute_value.toLowerCase() == 'true' ? true : false;
        }
      }
    }

    return groups;
  }


  /**
   * Преобразование типа string в требуемый тип значения
   * @param type string
   * @param value string
   * @returns any
   */
  attrValueTypeConversion(type: string, value: string): any {

    let res: any = '';

    if(type == this.attrValueType.string){
      res = value;
    }
    else if(type == this.attrValueType.boolean){

      if(value == null || value == undefined){
        res = false;
      }
      else{
        res = value.toLowerCase() == 'true' ? true : false;
      }
    }
    else if(type == this.attrValueType.integer){
      res = +value;
    }
    else if(type == this.attrValueType.numeric){
      res = +value;
    }
    else if(type == this.attrValueType.date){
      res = value;
    }
    else if(type == this.attrValueType.datetime){
      res = value;
    }
    else if(type == this.attrValueType.timestamp){
      res = this.datetimeService.convertStringToDate(value);
    }
    else{
      res = value;
    }

    return res;
  }


  /**
   * Преобразование типа в требуемый тип атрибута
   * @param type string
   * @param value string
   * @returns any
   */
  attrTypeConversion(type: string, value: string): any {

    let res: any = '';

    if(type == this.attrType.combobox_multiple){
      
      res = value != null && value != "" ? value.split(',') : [];
      
    }
    else{
      res = value;
    }
    
    return res;
  }


  /**
   * 
   * @returns Observable<any>
   */
  listenerSelectors(): Observable<any> {

    return this.selectorsSubject.asObservable();
  }

  listenerFocusMissingField(): Observable<any> {

    return this.focusMissingFieldSubject.asObservable();
  }

  /* Проверка правил атрибутов */

  /**
   * 
   * @param addinfoForm 
   * @param myAttr 
   * @returns 
   */
  checkRules(addinfoForm, myAttr: any): any {

    return (control: UntypedFormControl) => {

      let errMessage: string = '';
      let ruleStatus: boolean = true;
      let groups: any[] = addinfoForm.get('groups').value;
      let myValue: any = control.value;

      // правила валидации при взаимодействии с другими атрибутами
      if (myValue != null && myAttr.attribute_validation_rule != null) { 

        for (let ruleObj of [myAttr.attribute_validation_rule]) { // !!! attribute_validation_rule требуется подавать как массив
          
          let rule = ruleObj;
          let parentValue = null;
          let childValue = null;

          let group = groups.filter(val => val.attribute_group_id == rule.link.attribute_group_id);

          if (group.length > 0) {

            let attr = group[0].attributes.filter(val => val.attribute_id == rule.link.attribute_id);

            if (attr.length > 0) {

              if (attr[0].attribute_value != null) {

                if (rule.my_status == "parent") {

                  parentValue = myValue;
                  childValue = attr[0].attribute_value;
                }

                if (rule.my_status == "child") {

                  parentValue = attr[0].attribute_value;
                  childValue = myValue;
                }


                // *** подготовка занчений для вычесления по типу данных
                if (myAttr.attribute_type == this.attrType.datetime) {

                  parentValue = parentValue ? new Date(parentValue).getTime() : 0;
                  childValue = childValue ? new Date(childValue).getTime() : 0;
                }
                // ***

                // вычисление условия
                if (rule.condition != null) {

                  if (new Number(parentValue) instanceof Number && new Number(childValue) instanceof Number) {

                    let str = rule.condition;
                    let re1 = /@parent_val/gi;
                    let re2 = /@child_val/gi;
                    var newstr = str.replace(re1, String(parentValue)).replace(re2, String(childValue));

                    if (eval(newstr)) {

                      errMessage = '';
                      ruleStatus = true;
                    }
                    else {

                      errMessage = myAttr.validation_rule_err_message; // !!!
                      ruleStatus = false;
                      break;
                    }
                  }
                }
              }

              // действия в зависимости от результата вычисления условия
              if (ruleStatus && rule.action != null && rule.my_status == "parent") {

                let indexGroup = groups.findIndex(val => val.attribute_group_id == rule.link.attribute_group_id);

                if (indexGroup > -1) {

                  let indexAttr = groups[indexGroup].attributes.findIndex(val => val.attribute_id == rule.link.attribute_id);

                  if (indexAttr > -1) {

                    let field = addinfoForm.get(['groups', indexGroup, 'attributes', indexAttr, 'attribute_value']);
                    let value = field.value;

                    // ВАЖНО: Требуется для прохождения следуещего условия, и использования refreshAddInfoListAndSetSelectors
                    if (typeof value == 'boolean') {

                      value = value ? 'true' : 'false';
                    }

                    switch (rule.action) {

                      case 'refreshAddInfoListAndSetSelectors':
                        this.refreshAddInfoListAndSetSelectors(rule.link.attribute_group_id, rule.link.attribute_id, value);
                        break;
                      default:
                        break;
                    }

                    // if (value != null && value != undefined && value != '') {

                    //   switch (rule.action) {

                    //     case 'refreshAddInfoListAndSetSelectors':
                    //       this.refreshAddInfoListAndSetSelectors(rule.link.attribute_group_id, rule.link.attribute_id, value);
                    //       break;
                    //     default:
                    //       break;
                    //   }
                    // }
                    // else if (value == null || value == undefined || value == '') {

                    //   switch (rule.action) {

                    //     default:
                    //       break;
                    //   }
                    // }
                  }
                }
              }

            }
          }
        }
      }

      //control.markAsTouched();

      if (!ruleStatus) {

        return {
          checkRules: {
            valid: false,
            message: errMessage
          }
        };
      }

      return null;
    };
  }

  /* Методы для отработки правил */

  /**
   * 
   * @param groupId 
   * @param attributeId 
   * @param value 
   */
  refreshAddInfoListAndSetSelectors(groupId: string, attributeId: string, value: any){

    let currentValue = this.getCurrentValue(groupId, attributeId);
    let selectorValue: any;

    // ВАЖНО: корректировка значения после checkRules
    switch (value) {
      case 'true':
        selectorValue = true;
        break;
      case 'false':
        selectorValue = false;
        break;
      default:
        selectorValue = value;
        break;
    }
    
    if ((!this.selectors.hasOwnProperty(attributeId) || JSON.stringify(this.selectors[attributeId]) != JSON.stringify(selectorValue)) && (currentValue == null || currentValue.attribute_value != selectorValue)) { //
      
      this.selectors[attributeId] = selectorValue;
      this.selectorsSubject.next(this.selectors);
    }
  }


  /* Работа с текщеми данными */

  /**
   * 
   * @param form 
   * @returns 
   */
  preparationForCurrentValue(form: any): void {

    if(!form.groups) return;

    this.addinfoFormValue = form.groups; // <-- необходимо для использования во внешних компонентах

    for (let group of form.groups) {

      for (let attribute of group.attributes) {

        // если значение в поле есть, то добавляем в currentValue
        if (attribute.attribute_value != null && attribute.attribute_value != undefined) {

          this.setCurrentValue(group.attribute_group_id, attribute);
        }
        // если значение в поле нет,
        else {

          // то проверяем, есть ли этот атрибут в currentValue
          let index = this.currentValue.findIndex(val => (val.attribute_group_id == group.attribute_group_id && val.attribute_id == attribute.attribute_id));

          // если есть, то удаляем
          if (index > -1) {

            this.currentValue.splice(index, 1);
          }
        }
      }
    }
  }


  /**
   * 
   * @param groupId 
   * @param attribute 
   */
  setCurrentValue(groupId: string, attribute: any): void { // !!! подготовить модель для addinfoForm - attribute
    
    let index: number = this.currentValue.findIndex(val => (val.attribute_group_id == groupId && val.attribute_id == attribute.attribute_id));

    if (index > -1) {

      this.currentValue[index] = {...attribute};
      this.currentValue[index]['attribute_group_id'] = groupId;
    }
    else {

      this.currentValue.push({...attribute});
      this.currentValue[this.currentValue.length - 1]['attribute_group_id'] = groupId;
    }
  }


  /**
   * 
   * @param groupId 
   * @param attributeId 
   * @returns 
   */
  getCurrentValue(groupId: string, attributeId: string): any { // !!! подготовить модель для currentValue

    let index: number = this.currentValue.findIndex(val => (val.attribute_group_id == groupId && val.attribute_id == attributeId));

    return index > -1 ? {...this.currentValue[index]} : null;
  }


  /**
   *
   */
  removeCurrentValue(addinfoGroups: AddinfoGroup[]): void {

    for (let i = 0; i < this.currentValue.length; i++) {

      let groups = addinfoGroups.filter(val => val.attribute_group_id == this.currentValue[i].attribute_group_id);

      if (groups.length == 0) {

        this.currentValue.splice(i, 1);
      }
      else {

        let attributes = groups[0].attributes.filter(val => val.attribute_id == this.currentValue[i].attribute_id);

        if (attributes.length == 0) {

          this.currentValue.splice(i, 1);
        }
      }
    }
  }


  /**/

  /**
   * 
   * @returns any
   */
  getAdinfoFormValue(): any {

    return this.typeConversionToString(this.addinfoFormValue);
  }


  /**
   * Проверка обязательных полей перед сохранением.
   * Возвращает список имён незаполненных обязательных атрибутов.
   * @returns string[]
   */
  getMissingRequiredFields(): string[] {

    return this.collectMissingRequiredFields().map(field => field.attributeName);
  }

  getFirstMissingRequiredField(): any | null {

    const missing = this.collectMissingRequiredFields();
    return missing.length > 0 ? missing[0] : null;
  }

  focusFirstMissingRequiredField(): void {

    const firstMissing = this.getFirstMissingRequiredField();
    if (firstMissing) {
      this.focusMissingFieldSubject.next(firstMissing);
    }
  }


  /**
   * Проверка, пустое ли значение атрибута с учётом его типа.
   */
  private isAttrValueEmpty(attrType: string, value: any, filesLen: number, tagsLen: number): boolean {

    if (attrType === this.attrType.file || attrType === this.attrType.file_multiple) {
      return filesLen === 0;
    }

    if (attrType === this.attrType.tags) {
      return tagsLen === 0;
    }

    if (attrType === this.attrType.combobox_multiple) {
      return !Array.isArray(value) || value.length === 0;
    }

    if (attrType === this.attrType.date || attrType === this.attrType.datetime) {
      // значение для даты может прийти как объект {year,month,day} или строка
      if (value == null) return true;
      if (typeof value === 'string' && value.trim() === '') return true;
      if (typeof value === 'object' && !Array.isArray(value)) {
        return !(value.year || value.day || value.month);
      }
      return false;
    }

    if (value == null) return true;
    if (typeof value === 'string' && value.trim() === '') return true;

    return false;
  }

  private collectMissingRequiredFields(): any[] {

    const missing: any[] = [];

    if (!Array.isArray(this.addinfoGroupsSpec) || this.addinfoGroupsSpec.length === 0) {
      return missing;
    }

    const formGroups: any[] = Array.isArray(this.addinfoFormValue) ? this.addinfoFormValue : [];

    for (const specGroup of this.addinfoGroupsSpec) {

      if (!specGroup || !Array.isArray(specGroup.attributes)) continue;

      const formGroup = formGroups.find(g => g && g.attribute_group_id === specGroup.attribute_group_id);

      for (const specAttr of specGroup.attributes) {

        if (!specAttr || !specAttr.is_mandatory) continue;

        let value: any = null;
        let filesLen: number = 0;
        let tagsLen: number = 0;

        if (formGroup && Array.isArray(formGroup.attributes)) {

          const formAttr = formGroup.attributes.find(a => a && a.attribute_id === specAttr.attribute_id);

          if (formAttr) {

            value = formAttr.attribute_value;
            filesLen = Array.isArray(formAttr.files) ? formAttr.files.length : 0;
            tagsLen = Array.isArray(formAttr.tags) ? formAttr.tags.length : 0;
          }
        }

        if (this.isAttrValueEmpty(specAttr.attribute_type, value, filesLen, tagsLen)) {

          missing.push({
            groupId: specGroup.attribute_group_id,
            groupName: specGroup.attribute_group_name,
            attributeId: specAttr.attribute_id,
            attributeName: specAttr.attribute_name || specAttr.attribute_id
          });
        }
      }
    }

    return missing;
  }
}
